/* eslint-disable react-hooks/exhaustive-deps */
import { Box, Button, Dialog, DialogActions, DialogContent, IconButton, InputAdornment, Link, styled, TextField, Tooltip, Typography } from "@mui/material";
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import { useCallback, useEffect, useMemo, useState } from "react";
import { LoadingPage } from "../../../common/LoadingPage";
import { showNotification } from "../../../common/Notification/ToastifyNotification";
import { apiRequest } from "../../../common/common";
import DataTable from "../../../common/DataTable";
import { SupplierColDef } from "./columnDefs";
import { Check, DeleteOutline, EditOutlined } from "@mui/icons-material";
import ProductProvider from "./ProductModule/ProductProvider";
import CloudDownloadOutlinedIcon from '@mui/icons-material/CloudDownloadOutlined';
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { countries } from "../../../common/const";
import { useSelector } from "react-redux";

const DemoBox = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: 10
}));

const DemoBox1 = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 10,
    marginTop: 5
}));

const Supplier = () => {
    const { roles, user } = useSelector(state => state.users);
    const isUser = roles.find((a) => a.id === user?.role_id);
    const [gridApi, setGridApi] = useState(null);
    const [rowsPerPage, setRowPerPage] = useState(25);
    const [searchingText, setSearchText] = useState(null);
    const [dataLoading, setDataLoding] = useState(false);
    const [selectedData, setSelectedData] = useState([]);
    const [productProviderId, setProductProviderId] = useState(null);
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [filterType, setFilterType] = useState('All');
    const [dialogOpen, setDialogOpen] = useState(false);
    const [providerData, setProviderData] = useState(null);
    const [supplierData, setSupplierData] = useState([]);

    const onPaginationChanged = (event) => {
        if (event.api) {
            const newPageSize = event.api.paginationGetPageSize();
            setRowPerPage(newPageSize);
        }
    };

    const onGridReady = useCallback((params) => {
        if (params?.api) {
            const { api, searchingText } = params;
            setGridApi(api);
            setDataLoding(true);
            const dataSource = {
                rowCount: null,
                getRows: async (params) => {
                    const sortModel = params?.sortModel[0];
                    const sortBy = {
                        column: sortModel?.colId || '',
                        order: sortModel?.sort || '',
                    };
                    const url = [
                        `/product-provider?`,
                        `pageNo=${Math.floor((params.startRow / rowsPerPage) + 1)}`,
                        `pageSize=${rowsPerPage}`,
                        filterType !== 'All' ? filterType === 'orgVendor' ? `type=vender&organizationId=${user?.organization?.organization_id}` : `type=${filterType}` : '',
                        sortBy?.column ? `sortBy=${sortBy.column}` : '',
                        sortBy?.order ? `sortOrder=${sortBy.order}` : '',
                        searchingText ? `searchText=${encodeURIComponent(searchingText)}` : ''
                    ].filter(Boolean).join('&');
                    try {
                        const response = await apiRequest('get', `${url}`);
                        const { data, total_count } = response.data;
                        let lastRow = -1;
                        if (data?.length <= total_count) {
                            lastRow = total_count;
                        }
                        setSupplierData(data);
                        params.successCallback(data, lastRow);
                        setDataLoding(false);
                    } catch (error) {
                        setDataLoding(false);
                        showNotification(error?.response?.data.error || error.message, 'error');
                    }
                }
            };
            api.setGridOption("datasource", dataSource);
            setTimeout(() => {
                const node = api.getRenderedNodes();
                if (!node?.[0]?.displayed) {
                    api.showNoRowsOverlay();
                }
            }, 5000);
        }
    }, [rowsPerPage, filterType]);

    useEffect(() => {
        let isMounted = true;
        if (isMounted) {
            onGridReady({
                api: gridApi,
            });
        }
        return () => {
            isMounted = false;
        };
    }, [rowsPerPage, filterType]);

    const updatedColumns = useMemo(() => {
        const column = SupplierColDef;
        if (column.length > 0) {
            return column?.map((col) => {
                switch (col.field) {
                    case '':
                        return {
                            ...col,
                            hide: !['mvedasMasterAdmin', 'mvedasAdmin'].includes(isUser?.code),
                            checkboxSelection: true,
                            resizable: false,
                            sortable: false,
                            width: 60,
                            minWidth: 60
                        }
                    case 'action':
                        return {
                            ...col,
                            hide: !['mvedasMasterAdmin', 'mvedasAdmin'].includes(isUser?.code),
                            cellRenderer: (params) => (
                                <>
                                    <Tooltip title="Edit Supplier">
                                        <EditOutlined
                                            color="primary"
                                            style={{ cursor: 'pointer', color: selectedData.includes(params?.data?.product_provider_id) ? '' : 'gray' }}
                                            onClick={() => {
                                                if (selectedData.includes(params?.data?.product_provider_id)) {
                                                    setProviderData(params?.data);
                                                    setDialogOpen(true)
                                                }
                                            }}
                                        />
                                    </Tooltip>
                                    <Tooltip title="Delete Supplier">
                                        <DeleteOutline
                                            color="secondary"
                                            style={{ cursor: 'pointer', marginLeft: 5, color: selectedData.includes(params?.data?.product_provider_id) ? '' : 'gray' }}
                                            onClick={() => {
                                                if (selectedData.includes(params?.data?.product_provider_id)) {
                                                    setDeleteOpen(true);
                                                    setProductProviderId(params?.data?.product_provider_id);
                                                }
                                            }}
                                        />
                                    </Tooltip>
                                </>
                            ),
                        };
                    case 'manufacturer':
                        return {
                            ...col,
                            flex: 1,
                            minWidth: 120,
                            cellRenderer: (params) => params?.data && (
                                <>
                                    {params?.data?.type?.includes('manufacturer') ? (<Check color="primary" />) : '--'}
                                </>
                            ),
                        }
                    case 'vendor':
                        return {
                            ...col,
                            flex: 1,
                            minWidth: 120,
                            cellRenderer: (params) => params?.data && (
                                <>
                                    {params?.data?.access_level === 'public' && params?.data?.type?.includes('vender') ? (<Check color="primary" xx />) : '--'}
                                </>
                            ),
                        }
                    case 'org_vendor':
                        return {
                            ...col,
                            flex: 1,
                            minWidth: 120,
                            cellRenderer: (params) => params?.data && (
                                <>
                                    {params?.data?.access_level !== 'public' ? (<Check color="primary" xx />) : '--'}
                                </>
                            ),
                        }
                    case 'email':
                        return {
                            ...col,
                            cellRenderer: (params) => params?.data && (
                                <>
                                    {params?.data?.email !== 'null' ? params?.data?.email !== null ? params?.data?.email : '--' : '--'}
                                </>
                            ),
                        }
                    case 'contactno':
                        return {
                            ...col,
                            cellRenderer: (params) => params?.data && (
                                <>
                                    {params?.data?.contactno !== 'null' ? params?.data?.contactno !== null ? params?.data?.contactno : '--' : '--'}
                                </>
                            ),
                        }
                    case 'website':
                        return {
                            ...col,
                            cellRenderer: (params) => params?.data && (
                                <>
                                    {params?.data?.website !== 'null' ? params?.data?.website !== null ?
                                        <Link
                                            style={{ textDecoration: 'none', cursor: 'pointer' }}
                                            onClick={() => window.open(params?.data?.website, '_blank')}
                                        >
                                            {params?.data?.website}
                                        </Link>
                                        : '--' : '--'}
                                </>
                            ),
                        }
                    default:
                        return col;
                }
            });
        }
        return column;
    }, [SupplierColDef, selectedData]);

    return (
        <>
            <Dialog open={deleteOpen} fullWidth maxWidth="xs">
                <DialogContent>Are you sure you want to delete this one?</DialogContent>
                <DialogActions>
                    <Button size="small" variant="contained" color="secondary"
                        onClick={() => {
                            setProductProviderId(null);
                            setDeleteOpen(false);
                        }}
                    >
                        No
                    </Button>
                    <Button
                        size="small"
                        variant="contained"
                        color="primary"
                        onClick={async () => {
                            try {
                                setDataLoding(true);
                                let response = await apiRequest('delete', `/product-provider/${productProviderId}`);
                                if (response?.data) {
                                    setDataLoding(false);
                                    showNotification(response?.data?.message, 'success');
                                    setProductProviderId(null);
                                    setDeleteOpen(false);
                                    onGridReady({
                                        api: gridApi
                                    });
                                }
                            } catch (error) {
                                setDataLoding(false);
                                showNotification(error?.response?.data.error || error.message, 'error');
                            }
                        }}
                    >
                        Yes
                    </Button>
                </DialogActions>
            </Dialog>
            {dataLoading && (<LoadingPage isLoading={dataLoading} />)}
            <DemoBox1>
                <Box style={{ display: 'flex', alignItems: 'center' }}>
                    <Typography variant='h4' style={{ fontWeight: 'bold' }}>
                        Supplier
                    </Typography>
                    <Button
                        variant={filterType === 'All' ? 'contained' : 'outlined'}
                        size="small"
                        style={{
                            marginLeft: 5,
                            borderRadius: 20,
                            fontSize: '10px'
                        }}
                        onClick={() => {
                            setFilterType('All')
                        }}
                    >
                        All
                    </Button>
                    <Button
                        variant={filterType === 'manufacturer' ? 'contained' : 'outlined'}
                        size="small"
                        style={{
                            marginLeft: 5,
                            borderRadius: 20,
                            fontSize: '10px'
                        }}
                        onClick={() => {
                            setFilterType('manufacturer')
                        }}
                    >
                        Manufacturer
                    </Button>
                    <Button
                        variant={filterType === 'vender' ? 'contained' : 'outlined'}
                        size="small"
                        style={{
                            marginLeft: 5,
                            borderRadius: 20,
                            fontSize: '10px'
                        }}
                        onClick={() => {
                            setFilterType('vender')
                        }}
                    >
                        Vendor
                    </Button>
                    <Button
                        variant={filterType === 'orgVendor' ? 'contained' : 'outlined'}
                        size="small"
                        style={{
                            marginLeft: 5,
                            borderRadius: 20,
                            fontSize: '10px'
                        }}
                        onClick={() => {
                            setFilterType('orgVendor')
                        }}
                    >
                        Organization Vendor
                    </Button>
                </Box>
                <DemoBox>
                    {isUser?.code === 'mvedasMasterAdmin' || isUser?.code === 'mvedasAdmin' ? (
                        <Tooltip title="Download Supplier List">
                            <IconButton
                                onClick={async () => {
                                    const formattedData = supplierData.map((item) => ({
                                        "Name": item.name,
                                        "Manufacturer": item.type?.includes('manufacturer') ? '✓' : '--',
                                        "Vendor": item.type?.includes('vender') ? '✓' : '--',
                                        "Email": item.email,
                                        "Contact No": item.contactno,
                                        "Website": item.website,
                                        "Address 1": item.address_line_1,
                                        "Address 2": item.address_line_1,
                                        "City": item.city,
                                        "State": item.state,
                                        "Country": countries.find((c) => c.code === item.country?.toUpperCase())?.name,
                                        "Pin Code": item.pincode
                                    }));

                                    const worksheet = XLSX.utils.json_to_sheet(formattedData);

                                    const workbook = XLSX.utils.book_new();
                                    XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");

                                    const excelBuffer = XLSX.write(workbook, {
                                        bookType: "xlsx",
                                        type: "array"
                                    });

                                    const fileData = new Blob([excelBuffer], { type: "application/octet-stream" });
                                    saveAs(fileData, "Supplier List.xlsx");
                                }}
                            >
                                <CloudDownloadOutlinedIcon color="primary" />
                            </IconButton>
                        </Tooltip>) : ''}
                    <TextField
                        size="small"
                        id="outlined-search"
                        type="text"
                        placeholder="Search..."
                        style={{
                            background: '#ebeaea',
                        }}
                        value={searchingText}
                        onChange={(e) => setSearchText(e.target.value)}
                        onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                onGridReady({
                                    api: gridApi,
                                    searchingText: e.target.value
                                });
                            }
                        }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon fontSize="small" />
                                </InputAdornment>
                            ),
                            endAdornment: (
                                searchingText && ( // Show the clear icon only if there's text in the field
                                    <InputAdornment position="end">
                                        <IconButton
                                            onClick={() => {
                                                setSearchText('');
                                                onGridReady({
                                                    api: gridApi,
                                                    searchingText: ''
                                                });
                                            }}
                                        >
                                            <ClearIcon fontSize="small" />
                                        </IconButton>
                                    </InputAdornment>
                                )
                            ),
                        }}
                    />
                    {['mvedasMasterAdmin', 'mvedasAdmin'].includes(isUser?.code) && (
                        <Button
                            size="Medium"
                            variant="contained"
                            startIcon={<AddIcon />}
                            style={{
                                height: 39,
                            }}
                            onClick={() => setDialogOpen(true)}
                        >
                            Create
                        </Button>
                    )}
                </DemoBox>
            </DemoBox1>
            <ProductProvider
                dialogOpen={dialogOpen}
                setDialogOpen={setDialogOpen}
                fromSupplier
                providerData={providerData}
                onGridReady={() => {
                    onGridReady({
                        api: gridApi
                    });
                    setProviderData(null);
                }}
            />
            <DataTable
                height="74vh"
                columnDefs={updatedColumns}
                rowsPerPage={rowsPerPage}
                paginationPageSizeSelector={[10, 15, 25, 50, 100]}
                onPaginationChanged={onPaginationChanged}
                singleClickEdit={false}
                modelType="infinite"
                selectionType="single"
                onGridReady={onGridReady}
                fromTemplate={true}
                onSelectionChanged={() => {
                    if (gridApi) {
                        const selectedNodes = gridApi.getSelectedNodes();
                        setSelectedData(selectedNodes.map((node) => node.data.product_provider_id));
                    }
                }}
            />
        </>
    )
}

export default Supplier;